import {
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Audio,
  AudioListener,
  ACESFilmicToneMapping,
  EquirectangularReflectionMapping,
} from "three";
import EmptyWarehouseHDR from "/empty_warehouse_01_1k.hdr";
import {
  EJPlayerBase,
  EJWebGLRenderer,
  EJPlayerCubeOne,
  EJPlayer,
} from "./libs/ejx.es";
import { EJPlayerPipelineModuleEvents } from "./8thwall/ej-player-pipeline-module";
import mitt from "mitt";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

/**
 * Initialize the noAR scene
 * @param canvas HTMLCanvasElement
 */
export const initNoARScene = (canvas: HTMLCanvasElement) => {
  const emitter = mitt<EJPlayerPipelineModuleEvents>();
  let allowCanvasResize = true;
  let updatedCanvasWidth = 0;
  let updatedCanvasHeight = 0;

  const mediaRecorder = null;
  const listener = new AudioListener();
  const audio = new Audio(listener);
  const module = {
    emitter: emitter,
  };

  const scene = new Scene();
  const camera = new PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  );

  camera.position.set(0, 1.5, 0);
  scene.add(camera);
  const GLctx = canvas.getContext("webgl2");
  // const renderer = new WebGLRenderer({ canvas });
  const renderer = new EJWebGLRenderer({
    canvas,
    context: GLctx!,
  });
  renderer.autoClear = false;

  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setClearColor(0x000000, 0);

  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;

  const player = new EJPlayer({
    canvas,
    camera,
    renderer,
    scene,
  });
  if (player) {
    if (player.controls) {
      player.controls.dispose(); // remove any existing controls.
      player.controls = null;
    }
    if (player.orbitControls) {
      player.orbitControls.dispose(); // remove any existing orbit controls.
      player.orbitControls = null;
    }
    emitter.emit("setup", { player });
  }

  new RGBELoader().load(EmptyWarehouseHDR, function (texture) {
    texture.mapping = EquirectangularReflectionMapping;

    scene.environment = texture;
  });

  // document.body.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, canvas);

  const resize = () => {
    if (!player) {
      console.warn(
        "ejPlayerPipelineModule: onCanvasSizeChange but no `player` to update."
      );
      return;
    }
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;

    const devicePixelRatio = 1;
    const updateCameraAndRenderer = true;

    // temp hacky fix for the canvas resize breaking after an orientation change
    if (allowCanvasResize) {
      updatedCanvasWidth = canvasWidth;
      updatedCanvasHeight = canvasHeight;
    }
    player.resize(
      updatedCanvasWidth,
      updatedCanvasHeight,
      devicePixelRatio,
      updateCameraAndRenderer
    );
    emitter.emit("resized", {
      width: canvasWidth,
      height: canvasHeight,
      dpi: devicePixelRatio,
    });
    allowCanvasResize = true;
  };

  window.addEventListener("resize", () => {
    resize();
  });

  const animate = () => {
    requestAnimationFrame(animate);
    controls.update();

    // Render the scene
    if (!player) {
      console.warn(
        "ejPlayerPipelineModule: onRender but no `player` to update."
      );
      return;
    }

    player.render();
    player.renderer.clearDepth();
  };
  animate();

  return {
    _player: player,
    _audio: audio,
    _mediaRecorder: mediaRecorder,
    _resize: resize,
    _module: module,
  };
};
